import React, { useContext, useEffect, useRef } from 'react';
import { useAppDispatch } from 'redux/store';
import { AuthContext } from 'react-oauth2-code-pkce';
import { useNavigate } from 'react-router-dom';

import { loginSuccess } from '../../../redux/slices/auth';
import api from 'services/api'; // Your API service for backend calls
import { consumePostAuthRedirect } from 'utils/postAuthRedirect';
import { NO_TOKEN_CODE, loginFailureFrom, ssoFailureMessage } from './loginFailure';
import { LOGIN_TIMEOUT_MS } from './signInRequest';
import { log } from 'telemetry/logger';

const AuthCallback = () => {
  const authContext = useContext(AuthContext); // Access the context
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  /**
   * The last id token this component has already sent for validation.
   *
   * react-oauth2-code-pkce builds its context value inline in the provider's
   * JSX, so it is a fresh object on every render of the provider, and after
   * the code exchange the library renders at least twice with the token
   * present -- `handleTokenResponse` sets token/idToken, and a later `.finally`
   * sets `loginInProgress(false)`. Keying the effect on the token instead of
   * the context object narrows that; this ref closes the rest, because the two
   * renders carry the SAME token.
   *
   * Without it the validation posts twice, and a second attempt failing after
   * the first succeeded would navigate an already signed-in user to /login
   * reading "Single sign-on failed."
   */
  const validatedIdToken = useRef<string | null>(null);

  useEffect(() => {
    /**
     * Send the user back to where they can do something about it.
     *
     * This component has no other way to report: it is the OIDC redirect
     * target, so its whole render is "Authenticating...", and a failure that
     * only logged left the user reading that forever with the browser's back
     * button as the only exit (issue #1196). The message travels in router
     * state because the sign-in page is the thing with somewhere to put it.
     *
     * `replace` so the callback URL -- which carries a spent authorization
     * code -- is not left in history for the back button to re-enter. The
     * remembered post-auth redirect is deliberately NOT consumed, so a
     * successful retry still lands where the user was originally going.
     */
    const failSignIn = () => {
      navigate('/login', { replace: true, state: { authError: ssoFailureMessage() } });
    };

    const authenticateWithBackend = async (token: string) => {
      try {
        // Bounded like the password path: without a timeout this promise can
        // never settle, and this component's entire render is
        // "Authenticating..." -- so an unbounded wait is the one failure it
        // cannot report at all.
        const response = await api.post('/auth/validate-azuread-token', { token }, { timeout: LOGIN_TIMEOUT_MS });
        // Same hazard as the password path: `setToken(undefined)` stores the
        // STRING "undefined", which is truthy, so a 2xx with no token would
        // produce a session that looks valid and 401s on everything after.
        if (typeof response.data?.token !== 'string' || !response.data.token) {
          log.error('sso sign-in failed', { source: 'auth', status: response.status, code: NO_TOKEN_CODE });
          failSignIn();
          return;
        }
        // Dispatch the loginSuccess action with backend-verified user data
        dispatch(
          loginSuccess({
            token: response.data.token,
            refreshToken: response.data.refreshToken,
            // userData: response.data.user,  // Include user data from the backend
          })
        );
        localStorage.setItem('SSOLogin', 'true');
        api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        // Redirect after successful login
        navigate(consumePostAuthRedirect('/'), { replace: true });
      } catch (error) {
        // Pass the error through: `log.error`'s third argument goes to
        // serializeError, which keeps only name/message/stack -- so `config`
        // and `response` never reach telemetry, and dropping the error would
        // cost the message and stack for an SSO failure and buy nothing. The
        // status and code are added separately because they are not on the
        // Error itself. (The raw axios error IS still visible in devtools,
        // headers included; that is a support-screenshot concern, not a
        // telemetry one.)
        const failure = loginFailureFrom(error);
        log.error('sso sign-in failed', { source: 'auth', status: failure.status ?? 'unknown', code: failure.code ?? 'unknown' }, error);
        failSignIn();
      }
    };

    const idToken = authContext?.idToken;
    if (idToken) {
      // Validate each token exactly once -- see `validatedIdToken`.
      if (validatedIdToken.current === idToken) {
        return;
      }
      validatedIdToken.current = idToken;
      // Call the backend for further validation and user data retrieval
      authenticateWithBackend('Bearer ' + idToken);
    } else if (authContext?.error) {
      // Log the detail. react-oauth2-code-pkce only ever puts three things
      // here: the IdP's `error_description`, a FIXED cross-site-request-forgery
      // string from validateState (the received state is NOT interpolated), or
      // the message of a FetchError built from a non-ok token response. None
      // carries the authorization code, state value or redirect URI, and the
      // library already console.errors the same value itself on every one of
      // those paths. Without this, a failed SSO login is indistinguishable
      // between a state mismatch, an invalid_grant and a 503. Truncated as a
      // belt, and SECRET_VALUE_RE still redacts a token shape if an IdP embeds
      // one.
      log.error('sso provider error', { source: 'auth', detail: String(authContext.error).slice(0, 200) });
      failSignIn();
    } else {
      console.log('Waiting for token...');
    }
  }, [authContext?.idToken, authContext?.error, dispatch, navigate]);

  return <div>Authenticating...</div>;
};

export default AuthCallback;
